import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { environment } from '../../../../environments/environment';
import { TokenService } from './token.service';
import { Observable, tap } from 'rxjs';
import { Router } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';

@Injectable({
    providedIn: 'root'
})
export class AuthService {
    private http = inject(HttpClient);
    private tokenService = inject(TokenService);
    private cookieService = inject(CookieService);
    private router = inject(Router);

    private readonly authUrl = environment.authUrl;
    private readonly clientId = environment.clientId;
    private readonly clientSecret = environment.clientSecret;
    private readonly redirectUri = environment.redirectUri;
    private readonly postLogoutRedirectUri = environment.postLogoutRedirectUri;
    private readonly scope = environment.scope;
    private readonly endSessionEndpoint = `${environment.authUrl}/connect/logout`;

    login(targetUrl: string = '/', forcePrompt = false): void {
        const normalizedTargetUrl = this.normalizeTargetUrl(targetUrl);
        sessionStorage.setItem('target_url', normalizedTargetUrl);

        const params = new URLSearchParams({
            response_type: 'code',
            client_id: this.clientId,
            redirect_uri: this.redirectUri,
            scope: this.scope
        });

        if (forcePrompt) {
            params.set('prompt', 'login');
        }

        window.location.href = `${this.authUrl}/oauth2/authorize?${params.toString()}`;
    }

    handleCallback(code: string): Observable<any> {
        const body = new HttpParams()
            .set('grant_type', 'authorization_code')
            .set('client_id', this.clientId)
            .set('client_secret', this.clientSecret)
            .set('redirect_uri', this.redirectUri)
            .set('code', code);

        const headers = new HttpHeaders({
            'Content-Type': 'application/x-www-form-urlencoded'
        });

        return this.http.post(`${this.authUrl}/oauth2/token`, body.toString(), { headers }).pipe(
            tap((response: any) => {
                if (response.access_token) {
                    this.tokenService.setAccessToken(response.access_token);
                }
                if (response.refresh_token) {
                    this.tokenService.setRefreshToken(response.refresh_token);
                }
                if (response.id_token) {
                    this.tokenService.setIdToken(response.id_token);
                }

                const targetUrl = sessionStorage.getItem('target_url') || '/';
                sessionStorage.removeItem('target_url');
                this.router.navigateByUrl(targetUrl);
            })
        );
    }

    refreshToken(): Observable<any> {
        const refreshToken = this.tokenService.getRefreshToken();

        if (!refreshToken) {
            this.handleSessionExpired();
            throw new Error('No refresh token available.');
        }

        const body = new HttpParams()
            .set('grant_type', 'refresh_token')
            .set('client_id', this.clientId)
            .set('client_secret', this.clientSecret)
            .set('refresh_token', refreshToken);

        const headers = new HttpHeaders({
            'Content-Type': 'application/x-www-form-urlencoded'
        });

        return this.http.post(`${this.authUrl}/oauth2/token`, body.toString(), { headers }).pipe(
            tap((response: any) => {
                if (response.access_token) {
                    this.tokenService.setAccessToken(response.access_token);
                }
                if (response.refresh_token) {
                    this.tokenService.setRefreshToken(response.refresh_token);
                }
                if (response.id_token) {
                    this.tokenService.setIdToken(response.id_token);
                }
            })
        );
    }

    logout(): void {
        const idTokenHint = this.tokenService.getIdToken();
        this.clearAuthState();

        if (!idTokenHint) {
            window.location.assign(this.postLogoutRedirectUri);
            return;
        }

        const params = new URLSearchParams({
            id_token_hint: idTokenHint,
            post_logout_redirect_uri: this.postLogoutRedirectUri
        });

        window.location.assign(`${this.endSessionEndpoint}?${params.toString()}`);
    }

    handleSessionExpired(): void {
        const currentUrl = this.router.url || '/';
        this.clearAuthState();
        this.login(currentUrl);
    }

    private clearAuthState(): void {
        this.tokenService.clear();
        sessionStorage.removeItem('target_url');
        this.cookieService.deleteAll('/');
    }

    private normalizeTargetUrl(targetUrl: string): string {
        if (!targetUrl || targetUrl === '/callback' || targetUrl.startsWith('/callback?')) {
            return '/equipos';
        }

        return targetUrl;
    }
}
